/**
 * States Multi-Select Component
 * For selecting multiple US states
 */

import React, { useState } from "react";

interface StatesMultiSelectProps {
  selected: string[];
  onChange: (states: string[]) => void;
}

const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

const StatesMultiSelect: React.FC<StatesMultiSelectProps> = ({
  selected,
  onChange,
}) => {
  const [showAll, setShowAll] = useState(false);

  const handleToggle = (stateCode: string) => {
    if (selected.includes(stateCode)) {
      onChange(selected.filter((s) => s !== stateCode));
    } else {
      onChange([...selected, stateCode]);
    }
  };

  const handleSelectAll = () => {
    onChange(US_STATES.map((s) => s.code));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const displayedStates = showAll ? US_STATES : US_STATES.slice(0, 12);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          States ({selected.length} selected)
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Select All
          </button>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* State Pills */}
      <div className="flex flex-wrap gap-2">
        {displayedStates.map((state) => {
          const isSelected = selected.includes(state.code);
          return (
            <button
              key={state.code}
              type="button"
              onClick={() => handleToggle(state.code)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isSelected
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              title={state.name}
            >
              {state.code}
            </button>
          );
        })}
      </div>

      {/* Show More/Less Toggle */}
      {US_STATES.length > 12 && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          {showAll ? "Show Less" : `Show All ${US_STATES.length} States`}
        </button>
      )}

      {/* Selected States Summary */}
      {selected.length > 0 && (
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
          <strong>Selected:</strong>{" "}
          {selected
            .map((code) => US_STATES.find((s) => s.code === code)?.name || code)
            .join(", ")}
        </div>
      )}

      {selected.length === 0 && (
        <p className="text-sm text-gray-500 italic">
          No states selected. Voucher will be available in all states.
        </p>
      )}
    </div>
  );
};

export default StatesMultiSelect;
